import { DOCUMENT } from '@angular/common';
import { Service, inject, signal } from '@angular/core';

import { LocalStorageService } from '@nbx/core/storage';

import { LANGUAGES } from './token/language.token';

@Service()
export class LanguageService {
  private readonly _firstPosition: number = 0;

  private readonly _languageStorage: string = 'languageStorage';

  private readonly _defaultLanguage: string;

  private readonly _localStorageService: LocalStorageService;

  private readonly _languages: string[];

  private readonly _document: Document;

  private _availableLangs: string[] = [];

  public language = signal<string | null>(null);

  constructor() {
    this._localStorageService = inject(LocalStorageService);
    this._languages = inject<string[]>(LANGUAGES);
    this._defaultLanguage = this._languages[this._firstPosition];
    this._document = inject(DOCUMENT);
  }

  public updateLanguage(language: string): void {
    this._localStorageService.setItem(this._languageStorage, language);
    this.language.set(language);
    try {
      const expiry = new Date();
      expiry.setFullYear(expiry.getFullYear() + 1);
      this._document.cookie = `languageStorage=${language}; path=/; expires=${expiry.toUTCString()}; SameSite=Lax`;
    } catch {
      /* ignore — SSR or restricted context */
    }
  }

  public setLanguages(availableLangs: string[]): void {
    this._availableLangs = availableLangs;
  }

  public getLanguage(): string {
    if (!this.language()) {
      const language = this.getStorageLanguage() ?? this._defaultLanguage;
      this.language.set(language);
    }
    return this.language()!;
  }

  private getStorageLanguage(): string | undefined {
    const language = this._localStorageService.getItem(this._languageStorage)!;
    return this.isAcceptedLanguage(language) ? language : undefined;
  }

  private isAcceptedLanguage(language: string): boolean {
    return this._availableLangs.includes(language);
  }
}
